<template>
  <v-card class="pb-8" :loading="loading">
    <h3 class="text-center mb-4 pt-8">{{ title }}</h3>

    <!-- Error Alert -->
    <v-alert v-if="error.show" :type="error.type" dismissible @input="error.show = false" class="ma-4">
      {{ error.message }}
    </v-alert>

    <v-form ref="form" @submit.prevent class="pa-4">
      <v-card>
        <v-tabs v-model="step" grow background-color="primary" dark>
          <v-tab>مشخصات</v-tab>
          <v-tab :disabled="!itemId && step !== 1">تصاویر</v-tab>
          <v-tab :disabled="!itemId && step !== 2">تنوع</v-tab>
        </v-tabs>

        <v-tabs-items v-model="step" touchless>
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-text-field v-model="item.title" @input="updateSlug" label="عنوان" :rules="required_rule" required />
                <v-text-field v-model="item.slug" label="اسلاگ" :rules="slug_rule" required />
                <v-autocomplete
                  v-model="item.categories"
                  :items="categoriesArrayList"
                  :rules="required_rule"
                  required
                  label="دسته بندی"
                  item-text="name"
                  deletable-chips
                  item-value="id"
                  multiple
                  chips
                />

                <v-textarea v-model="item.description" label="توضیحات" rows="3" :rules="required_rule" required />

                <v-text-field v-model="item.dimensions" label="ابعاد" />
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-row>
                  <template v-if="itemId && item.images && item.images?.length">
                    <v-col cols="12">
                      <div class="d-flex justify-space-between align-center mt-4">
                        <h3>تصاویر موجود</h3>
                        <span class="text-caption grey--text"><v-icon small>mdi-drag</v-icon> برای تغییر ترتیب، تصاویر را بکشید</span>
                      </div>
                    </v-col>

                    <draggable
                      v-model="item.images"
                      @start="onDragStart"
                      @end="onDragEnd"
                      class="row ma-0"
                      handle=".drag-handle"
                      ghost-class="ghost-image"
                      drag-class="dragging-image"
                      chosen-class="chosen-image"
                      animation="200"
                      force-fallback
                    >
                      <v-col
                        class="d-flex child-flex position-relative image-col"
                        cols="6"
                        sm="4"
                        md="3"
                        lg="2"
                        v-for="(imageUrl, index) in item.images"
                        :key="imageUrl"
                      >
                        <v-hover v-slot="{ hover }">
                          <div style="position: relative; width: 100%; height: 100%">
                            <v-img aspect-ratio="1" :src="imageUrl" class="image-item" :class="{ 'image-hover': hover }" />

                            <!-- Order Badge -->
                            <v-chip
                              x-small
                              color="primary"
                              style="position: absolute; top: 4px; left: 4px; z-index: 3; font-size: 10px; height: 18px"
                            >
                              {{ index + 1 }}
                            </v-chip>

                            <!-- Drag Handle -->
                            <div
                              class="drag-handle"
                              style="
                                position: absolute;
                                top: 4px;
                                right: 4px;
                                z-index: 3;
                                cursor: move;
                                background: rgba(255, 255, 255, 0.9);
                                border-radius: 4px;
                                padding: 2px;
                              "
                            >
                              <v-icon color="primary" small>mdi-drag</v-icon>
                            </div>

                            <!-- Delete Button - No overlay, just the button -->
                            <v-fade-transition>
                              <v-btn
                                v-if="hover"
                                dark
                                fab
                                x-small
                                color="error"
                                @click="handleDeleteImage(imageUrl)"
                                style="position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); z-index: 3"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </v-fade-transition>
                          </div>
                        </v-hover>
                      </v-col>
                    </draggable>
                  </template>
                </v-row>
                <v-file-input
                  v-model="images"
                  label="بارگذاری تصویر"
                  multiple
                  prepend-icon="mdi-camera"
                  @change="handleFileChange"
                  accept="image/*"
                  :disabled="loading"
                />
                <div v-if="images && images.length > 0" class="mt-4">
                  <v-alert type="info" outlined>
                    <div class="d-flex align-center">
                      <v-icon left>mdi-information</v-icon>
                      <span>{{ images.length }} تصویر انتخاب شده است. برای بارگذاری روی دکمه پایین کلیک کنید.</span>
                    </div>
                  </v-alert>
                </div>

                <!-- Upload Progress -->
                <div v-if="uploadProgress.show" class="mt-4">
                  <v-card outlined>
                    <v-card-text>
                      <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-subtitle-2">در حال بارگذاری تصاویر...</span>
                        <span class="text-caption">{{ uploadProgress.current }} از {{ uploadProgress.total }}</span>
                      </div>
                      <v-progress-linear :value="(uploadProgress.current / uploadProgress.total) * 100" height="25" color="primary" striped>
                        <template v-slot:default="{ value }">
                          <strong>{{ Math.ceil(value) }}%</strong>
                        </template>
                      </v-progress-linear>
                      <div class="d-flex justify-space-between mt-2">
                        <span class="text-caption success--text">✓ موفق: {{ uploadProgress.successful }}</span>
                        <span v-if="uploadProgress.failed > 0" class="text-caption error--text">✗ ناموفق: {{ uploadProgress.failed }}</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <div class="d-flex">
                  <v-row>
                    <v-col class="d-flex child-flex position-relative" cols="3" v-for="(imageUrl, index) in imageUrls" :key="`img-col-${index}`">
                      <v-hover v-slot="{ hover }">
                        <div>
                          <v-img aspect-ratio="1" :src="imageUrl" :key="`image-${index}`" />
                          <v-fade-transition>
                            <v-overlay v-if="hover" absolute color="#036358" class="pa-4">
                              <v-btn dark fab small @click="handleDeleteFile(index)">
                                <v-icon>mdi-delete</v-icon>
                              </v-btn>
                            </v-overlay>
                          </v-fade-transition>
                        </div>
                      </v-hover>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <div class="d-flex justify-end mb-6">
                  <v-btn color="primary" @click="addVariation"><v-icon class="ml-2">mdi-plus</v-icon>افزودن تنوع</v-btn>
                </div>

                <template v-if="item.variations.length">
                  <v-card v-for="(variation, index) in item.variations" :key="`variation-card-${index}`" class="mb-4" outlined>
                    <v-card-text>
                      <div class="d-flex justify-space-between align-center mb-2">
                        <h4 class="text-h6">تنوع شماره {{ index + 1 }}</h4>
                        <div class="d-flex ga-2">
                          <v-btn color="primary" icon small @click="copyVariation(index)" title="کپی">
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                          <v-btn color="error" icon small @click="removeVariation(index, variation.id)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <v-row>
                        <v-col cols="12" sm="6" md="3">
                          <v-text-field
                            :value="variation.price"
                            @input="handlePriceInput(variation, $event)"
                            label="قیمت"
                            :rules="price_rule"
                            required
                            outlined
                            dense
                          />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <v-autocomplete
                            v-model="variation.colors"
                            :items="colorOptions"
                            label="رنگ‌ها"
                            :rules="required_rule"
                            required
                            outlined
                            dense
                            item-text="name"
                            item-value="name"
                            return-object
                            multiple
                            chips
                            deletable-chips
                            @change="onColorsChange(variation, $event)"
                          >
                            <template v-slot:item="{ item }">
                              <div class="d-flex align-center">
                                <div class="color-preview mr-3" :style="{ backgroundColor: item.hex }"></div>
                                <span>{{ item.name }}</span>
                              </div>
                            </template>
                            <template v-slot:selection="{ item }">
                              <v-chip small class="mr-1">
                                <div class="d-flex align-center">
                                  <div class="color-preview mr-1" :style="{ backgroundColor: item.hex }"></div>
                                  <span>{{ item.name }}</span>
                                </div>
                              </v-chip>
                            </template>
                          </v-autocomplete>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <v-select
                            v-model="variation.material_id"
                            :items="materialsArrayList"
                            label="ماده"
                            item-text="name"
                            item-value="id"
                            outlined
                            dense
                            clearable
                          />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <v-text-field v-model="variation.size" label="اندازه" outlined dense />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6" md="3">
                          <div class="d-flex flex-column align-center justify-center h-100">
                            <span class="text-caption mb-2">وضعیت</span>
                            <v-switch v-model="variation.active" color="primary" :label="variation.active ? 'فعال' : 'غیرفعال'" />
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </template>

                <template v-else>
                  <v-card class="text-center pa-8" outlined>
                    <v-card-text>
                      <v-icon size="64" color="grey lighten-1">mdi-package-variant</v-icon>
                      <h3 class="text-h6 mt-4 mb-2">هیچ تنوعی وجود ندارد</h3>
                      <p class="text-body-2 grey--text">برای افزودن تنوع محصول، روی دکمه بالا کلیک کنید</p>
                    </v-card-text>
                  </v-card>
                </template>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <v-divider class="my-6" />
      <div class="d-flex justify-end">
        <v-btn color="secondary" text @click="close" class="mx-2"> بستن </v-btn>
        <v-btn color="primary" type="submit" @click="submitForm" :loading="loading">{{ buttonText }} </v-btn>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'

export default {
  components: {
    draggable,
  },

  props: {
    itemId: {
      type: [String, Number],
      default: null,
    },
    copyFromId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      item: {
        title: '',
        slug: '',
        description: '',
        dimensions: '',
        categories: [],
        variations: [],
        images: [],
      },
      dialog: true,
      step: 0,
      images: [],
      loading: false,
      createdItemId: '',
      error: {
        message: '',
        show: false,
        type: 'error', // 'error', 'warning', 'info'
      },
      validationErrors: {},
      uploadProgress: {
        show: false,
        current: 0,
        total: 0,
        failed: 0,
        successful: 0,
      },
    }
  },

  computed: {
    ...mapGetters({
      categoriesList: 'getCategories',
      colorsList: 'getColors',
      materialsList: 'getMaterials',
    }),

    // recrusive function to get all categories and its children in one array
    categoriesArrayList() {
      let list = []
      let getChildren = (category) => {
        list.push(category)
        if (category.children) {
          category.children.forEach((child) => {
            getChildren(child)
          })
        }
      }
      this.categoriesList.forEach((category) => {
        getChildren(category)
      })
      return list
    },

    colorsArrayList() {
      const colors = this.colorsList || []
      return colors.map((item) => ({
        id: item.id,
        name: item.name,
      }))
    },

    colorOptions() {
      const colors = this.colorsList || []
      return colors.map((color) => ({
        id: color.id,
        name: color.name,
        hex: color.hex || '#000000',
      }))
    },

    materialsArrayList() {
      const materials = this.materialsList || []
      return materials.map((material) => ({
        id: material.id,
        name: material.name,
      }))
    },

    title() {
      if (this.copyFromId && !this.itemId) {
        return 'کپی محصول'
      }
      return this.itemId ? 'ویرایش محصول' : 'افزودن محصول جدید'
    },

    required_rule() {
      return [
        (v) => {
          // Handle null/undefined values
          if (v === null || v === undefined) {
            return 'نمی‌تواند خالی باشد'
          }

          // Handle strings
          if (typeof v === 'string') {
            return v.trim().length > 0 || 'نمی‌تواند خالی باشد'
          }

          // Handle numbers
          if (typeof v === 'number') {
            return v.toString().length > 0 || 'نمی‌تواند خالی باشد'
          }

          // Handle arrays
          if (Array.isArray(v)) {
            return v.length > 0 || 'نمی‌تواند خالی باشد'
          }

          // Handle other types
          return true
        },
      ]
    },

    price_rule() {
      return [
        (v) => {
          if (v === null || v === undefined || v === '') {
            return 'قیمت الزامی است'
          }
          return true
        },
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Skip validation if empty (handled by required rule)
          }
          // Convert Persian numbers to English before validation
          const englishValue = this.persianToEnglish(v)
          return !isNaN(englishValue) || 'قیمت باید عدد باشد'
        },
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Skip validation if empty
          }
          // Convert Persian numbers to English before validation
          const englishValue = this.persianToEnglish(v)
          return parseFloat(englishValue) >= 0 || 'قیمت نمی‌تواند منفی باشد'
        },
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Skip validation if empty
          }
          // Convert Persian numbers to English before validation
          const englishValue = this.persianToEnglish(v)
          return parseFloat(englishValue) <= 999999999 || 'قیمت بیش از حد مجاز است'
        },
      ]
    },

    stock_rule() {
      return [
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Stock is optional
          }
          // Convert Persian numbers to English before validation
          const englishValue = this.persianToEnglish(v)
          return !isNaN(englishValue) || 'تعداد موجودی باید عدد باشد'
        },
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Skip validation if empty
          }
          // Convert Persian numbers to English before validation
          const englishValue = this.persianToEnglish(v)
          return parseFloat(englishValue) >= 0 || 'تعداد موجودی نمی‌تواند منفی باشد'
        },
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Skip validation if empty
          }
          // Convert Persian numbers to English before validation
          const englishValue = this.persianToEnglish(v)
          return parseFloat(englishValue) <= 999999 || 'تعداد موجودی بیش از حد مجاز است'
        },
      ]
    },

    slug_rule() {
      return [
        (v) => {
          if (v === null || v === undefined || v === '') {
            return 'اسلاگ الزامی است'
          }
          return true
        },
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Skip validation if empty (handled by required rule)
          }
          return (
            /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-z0-9\s-]+$/.test(v) ||
            'اسلاگ می‌تواند شامل حروف فارسی، انگلیسی، اعداد، فاصله و خط تیره باشد'
          )
        },
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Skip validation if empty
          }
          return v.length >= 3 || 'اسلاگ باید حداقل ۳ کاراکتر باشد'
        },
        (v) => {
          if (v === null || v === undefined || v === '') {
            return true // Skip validation if empty
          }
          return v.length <= 100 || 'اسلاگ نباید بیش از ۱۰۰ کاراکتر باشد'
        },
      ]
    },

    imageUrls() {
      return this.images.map((image) => URL.createObjectURL(image))
    },

    buttonText() {
      const hasSelectedImages = this.images && this.images.length > 0
      const imageCount = hasSelectedImages ? this.images.length : 0

      if (this.itemId) {
        // Edit mode
        switch (this.step) {
          case 0:
            return 'ویرایش'
          case 1:
            return hasSelectedImages ? `بارگذاری ${imageCount} تصویر` : 'بارگذاری تصاویر'
          default:
            return 'ذخیره تنوع ها'
        }
      }

      // Create mode
      switch (this.step) {
        case 0:
          return 'ادامه'
        case 1:
          return hasSelectedImages ? `بارگذاری ${imageCount} تصویر` : 'ادامه'
        default:
          return 'ذخیره'
      }
    },
  },

  methods: {
    // Utility functions for Persian number conversion
    persianToEnglish(str) {
      if (!str) return str
      const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
      const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
      const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']

      let result = str.toString()

      // Convert Persian numbers
      for (let i = 0; i < persianNumbers.length; i++) {
        result = result.replace(new RegExp(persianNumbers[i], 'g'), englishNumbers[i])
      }

      // Convert Arabic numbers
      for (let i = 0; i < arabicNumbers.length; i++) {
        result = result.replace(new RegExp(arabicNumbers[i], 'g'), englishNumbers[i])
      }

      return result
    },

    englishToPersian(str) {
      if (!str) return str
      const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
      const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

      let result = str.toString()

      for (let i = 0; i < englishNumbers.length; i++) {
        result = result.replace(new RegExp(englishNumbers[i], 'g'), persianNumbers[i])
      }

      return result
    },

    handlePriceInput(variation, value) {
      const persianValue = value
      const englishValue = this.persianToEnglish(persianValue)
      variation.price = englishValue
    },

    handleStockInput(variation, value) {
      const persianValue = value
      const englishValue = this.persianToEnglish(persianValue)
      variation.stock = englishValue
    },

    submitForm() {
      console.log('Submit form called - Step:', this.step, 'ItemId:', this.itemId, 'Images:', this.images?.length)

      if (!this.$refs.form.validate()) {
        this.showError('لطفاً تمام فیلدهای الزامی را پر کنید', 'warning')
        return
      }

      this.hideError()

      if (this.itemId) {
        // Edit mode
        if (this.step === 0) {
          this.update()
        } else if (this.step === 1) {
          if (!this.images || this.images.length === 0) {
            this.showError('لطفاً حداقل یک تصویر انتخاب کنید', 'warning')
            return
          }
          this.uploadImages()
        } else if (this.step === 2) {
          // Check if we have new variations (without IDs) or existing variations
          const hasNewVariations = this.item.variations.some((v) => !v.id)
          if (hasNewVariations) {
            this.postVariations() // Add new variations
          } else {
            this.updateVariations() // Update existing variations
          }
        }
        return
      }

      // Create mode
      if (this.step === 0) {
        this.createProduct()
      } else if (this.step === 1) {
        this.uploadImages()
      } else if (this.step === 2) {
        this.postVariations()
      }
    },

    showError(message, type = 'error') {
      this.error = {
        message,
        show: true,
        type,
      }
    },

    hideError() {
      this.error.show = false
    },

    handleApiError(error, defaultMessage = 'خطایی رخ داده است') {
      console.error('API Error:', error)

      if (error.response) {
        // Server responded with error status
        const status = error.response.status
        const data = error.response.data

        switch (status) {
          case 400:
            this.showError(data.message || 'اطلاعات ارسالی نامعتبر است', 'error')
            break
          case 401:
            this.showError('شما اجازه دسترسی ندارید', 'error')
            break
          case 403:
            this.showError('شما اجازه انجام این عمل را ندارید', 'error')
            break
          case 404:
            this.showError('محصول مورد نظر یافت نشد', 'error')
            break
          case 422:
            this.showError(data.message || 'اطلاعات ارسالی نامعتبر است', 'warning')
            if (data.errors) {
              this.validationErrors = data.errors
            }
            break
          case 500:
            this.showError('خطای سرور. لطفاً بعداً تلاش کنید', 'error')
            break
          default:
            this.showError(data.message || defaultMessage, 'error')
        }
      } else if (error.request) {
        // Network error
        this.showError('خطای شبکه. لطفاً اتصال اینترنت خود را بررسی کنید', 'error')
      } else {
        // Other error
        this.showError(defaultMessage, 'error')
      }
    },

    close() {
      this.$emit('close')
    },

    updateSlug() {
      if (this.item.title) {
        // Convert Persian/Arabic text to a URL-friendly format
        let slug = this.item.title
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-z0-9-]/g, '') // Remove special characters except Persian, English, numbers, and hyphens
          .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
          .replace(/^(-|$)/g, '') // Remove leading/trailing hyphens

        // Convert English characters to lowercase
        slug = slug.replace(/[a-z]/g, (char) => char.toLowerCase())

        this.item.slug = slug
      }
    },

    createProduct() {
      this.loading = true
      this.$store
        .dispatch('saveProduct', this.item)
        .then((res) => {
          this.createdItemId = res.data.id
          this.step = 1
          this.showError('محصول با موفقیت ایجاد شد', 'success')
        })
        .catch((error) => {
          this.handleApiError(error, 'خطا در ایجاد محصول')
        })
        .finally(() => (this.loading = false))
    },

    // Helper function to delay between uploads
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },

    // Helper function to upload a single image with retry logic
    async uploadSingleImageWithRetry(formData, imageName, maxRetries = 3) {
      let lastError = null

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          await this.$store.dispatch('uploadImage', formData)
          return { success: true }
        } catch (error) {
          lastError = error
          console.warn(`Upload attempt ${attempt}/${maxRetries} failed for ${imageName}:`, error)

          // Don't retry on client-side errors (4xx) except 429 (rate limit)
          if (error.response) {
            const status = error.response.status
            if (status >= 400 && status < 500 && status !== 429) {
              // Client error, don't retry
              break
            }
          }

          // Wait before retrying (exponential backoff)
          if (attempt < maxRetries) {
            const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000) // Max 5 seconds
            console.log(`Retrying in ${delayMs}ms...`)
            await this.delay(delayMs)
          }
        }
      }

      return { success: false, error: lastError }
    },

    async uploadImages() {
      if (!this.images || this.images.length === 0) {
        this.showError('لطفاً حداقل یک تصویر انتخاب کنید', 'warning')
        return
      }

      // Validate image files
      const maxSize = 10 * 1024 * 1024 // 10MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

      for (let image of this.images) {
        if (image.size > maxSize) {
          this.showError('حجم تصاویر نباید بیش از ۱۰ مگابایت باشد', 'warning')
          return
        }
        if (!allowedTypes.includes(image.type)) {
          this.showError('فرمت تصاویر باید JPEG، PNG، GIF یا WebP باشد', 'warning')
          return
        }
      }

      // Initialize progress tracking
      this.uploadProgress = {
        show: true,
        current: 0,
        total: this.images.length,
        failed: 0,
        successful: 0,
      }

      this.loading = true
      const productId = this.itemId || this.createdItemId
      const imagesToUpload = [...this.images] // Create a copy

      // Upload images one by one (sequential) with delay between uploads
      for (let i = 0; i < imagesToUpload.length; i++) {
        const image = imagesToUpload[i]

        try {
          // Create FormData for single image
          const formData = new FormData()
          formData.append('product_id', productId)
          formData.append('images[]', image)

          console.log(`Uploading image ${i + 1}/${imagesToUpload.length}:`, image.name)

          // Upload single image with retry logic
          const result = await this.uploadSingleImageWithRetry(formData, image.name)

          if (result.success) {
            this.uploadProgress.successful++
            console.log(`Image ${i + 1} uploaded successfully`)
          } else {
            console.error(`Failed to upload image ${i + 1} after retries:`, result.error)
            this.uploadProgress.failed++
          }
        } catch (error) {
          console.error(`Failed to upload image ${i + 1}:`, error)
          this.uploadProgress.failed++
        }

        // Update current progress
        this.uploadProgress.current = i + 1

        // Add a small delay between uploads to avoid rate limiting (except for the last image)
        if (i < imagesToUpload.length - 1) {
          await this.delay(300) // 300ms delay between uploads
        }
      }

      this.loading = false

      // Show results
      if (this.uploadProgress.failed === 0) {
        this.showError(`همه تصاویر (${this.uploadProgress.successful}) با موفقیت بارگذاری شدند`, 'success')
      } else if (this.uploadProgress.successful > 0) {
        this.showError(`${this.uploadProgress.successful} تصویر موفق و ${this.uploadProgress.failed} تصویر ناموفق`, 'warning')
      } else {
        this.showError('بارگذاری تمام تصاویر ناموفق بود', 'error')
      }

      // Clear the images array after upload attempt
      this.images = []

      // Hide progress after 3 seconds
      setTimeout(() => {
        this.uploadProgress.show = false
      }, 3000)

      // Fetch updated product data to show new images
      if (this.uploadProgress.successful > 0) {
        if (this.itemId) {
          this.fetchUpdatedProduct()
        } else {
          this.step = 2
        }
      }
    },

    postVariations() {
      if (!this.item.variations || this.item.variations.length === 0) {
        this.showError('لطفاً حداقل یک تنوع محصول اضافه کنید', 'warning')
        return
      }

      // Validate variations
      for (let i = 0; i < this.item.variations.length; i++) {
        const variation = this.item.variations[i]
        if (!variation.price || !variation.colors || !Array.isArray(variation.colors) || variation.colors.length === 0) {
          this.showError(`تنوع شماره ${i + 1}: قیمت و حداقل یک رنگ الزامی است`, 'warning')
          return
        }
      }

      // Transform variations for API - convert colors array to color names array
      const variationsPayload = this.item.variations.map((variation) => {
        const variationData = {
          price: variation.price,
          stock: 1, // Default stock value
          active: variation.active !== undefined ? variation.active : true,
          size: variation.size || null,
          material_id: variation.material_id || null,
          weight: variation.weight || null,
        }

        // Convert colors array to array of color names
        if (variation.colors && Array.isArray(variation.colors) && variation.colors.length > 0) {
          variationData.colors = variation.colors.map((color) => {
            // If color is an object, get the name, otherwise it's already a string
            return typeof color === 'object' ? color.name : color
          })
        }

        return variationData
      })

      let payload = {
        id: this.itemId || this.createdItemId, // Use itemId for edit mode, createdItemId for create mode
        variations: variationsPayload,
      }
      this.loading = true
      this.$store
        .dispatch('saveVariations', payload)
        .then(() => {
          this.showError('تنوع‌های محصول با موفقیت ذخیره شدند', 'success')
          // Clear form and close after successful creation to allow creating another product
          setTimeout(() => {
            this.clearForm()
            this.close()
          }, 1500) // Give user time to see success message
        })
        .catch((error) => {
          this.handleApiError(error, 'خطا در ذخیره تنوع‌های محصول')
        })
        .finally(() => (this.loading = false))
    },

    update() {
      this.loading = true
      this.$store
        .dispatch('updateProduct', this.item)
        .then(() => {
          this.showError('محصول با موفقیت بروزرسانی شد', 'success')
          this.close()
        })
        .catch((error) => {
          this.handleApiError(error, 'خطا در بروزرسانی محصول')
        })
        .finally(() => (this.loading = false))
    },

    updateVariations() {
      if (!this.item.variations || this.item.variations.length === 0) {
        this.showError('لطفاً حداقل یک تنوع محصول اضافه کنید', 'warning')
        return
      }

      // Validate variations
      for (let i = 0; i < this.item.variations.length; i++) {
        const variation = this.item.variations[i]
        if (!variation.price || !variation.colors || !Array.isArray(variation.colors) || variation.colors.length === 0) {
          this.showError(`تنوع شماره ${i + 1}: قیمت و حداقل یک رنگ الزامی است`, 'warning')
          return
        }
      }

      // Transform variations for API - convert colors array to color names array
      const variationsPayload = this.item.variations.map((variation) => {
        const variationData = {
          price: variation.price,
          stock: 1, // Default stock value
          active: variation.active !== undefined ? variation.active : true,
          size: variation.size || null,
          material_id: variation.material_id || null,
          weight: variation.weight || null,
        }

        // Convert colors array to array of color names
        if (variation.colors && Array.isArray(variation.colors) && variation.colors.length > 0) {
          variationData.colors = variation.colors.map((color) => {
            // If color is an object, get the name, otherwise it's already a string
            return typeof color === 'object' ? color.name : color
          })
        }

        return variationData
      })

      let payload = {
        id: this.itemId,
        variations: variationsPayload,
      }
      this.loading = true
      this.$store
        .dispatch('saveVariations', payload) // Use saveVariations instead of updateVariations
        .then(() => {
          this.showError('تنوع‌های محصول با موفقیت بروزرسانی شدند', 'success')
          // Clear form and close after successful update
          setTimeout(() => {
            this.clearForm()
            this.close()
          }, 1500) // Give user time to see success message
        })
        .catch((error) => {
          this.handleApiError(error, 'خطا در بروزرسانی تنوع‌های محصول')
        })
        .finally(() => (this.loading = false))
    },

    handleDeleteFile(index) {
      this.images.splice(index, 1)
    },

    addVariation() {
      try {
        // Ensure variations array exists
        if (!this.item.variations) {
          this.item.variations = []
        }

        // Add new variation
        this.item.variations.push({
          id: null, // New variations don't have an ID yet
          price: '',
          colors: [],
          material_id: null,
          size: '',
          stock: 1, // Default stock value
          active: true,
        })

        // Show success message
        this.showError('تنوع جدید اضافه شد', 'success')
      } catch (error) {
        console.error('Error adding variation:', error)
        this.showError('خطا در افزودن تنوع جدید', 'error')
      }
    },

    copyVariation(index) {
      try {
        // Ensure variations array exists
        if (!this.item.variations || index < 0 || index >= this.item.variations.length) {
          this.showError('تنوع مورد نظر یافت نشد', 'error')
          return
        }

        const sourceVariation = this.item.variations[index]

        // Deep clone the variation to avoid reference issues
        const copiedVariation = {
          id: null, // New variation, no ID yet
          price: sourceVariation.price || '',
          colors: sourceVariation.colors ? structuredClone(sourceVariation.colors) : [],
          material_id: sourceVariation.material_id || null,
          size: sourceVariation.size || '',
          stock: sourceVariation.stock || 1,
          active: typeof sourceVariation.active === 'boolean' ? sourceVariation.active : true,
        }

        // Add the copied variation after the source variation
        this.item.variations.splice(index + 1, 0, copiedVariation)

        // Show success message
        this.showError('تنوع با موفقیت کپی شد', 'success')
      } catch (error) {
        console.error('Error copying variation:', error)
        this.showError('خطا در کپی کردن تنوع', 'error')
      }
    },

    removeVariation(index, id) {
      // Store the variation before removing it (in case we need to restore it)
      const removedVariation = this.item.variations[index]

      // Remove from UI immediately
      this.item.variations.splice(index, 1)

      // If variation has an ID, it exists on server and needs to be deleted
      if (id) {
        this.loading = true
        this.$store
          .dispatch('deleteVariation', id)
          .then(() => {
            this.showError('تنوع با موفقیت حذف شد', 'success')
          })
          .catch((error) => {
            this.handleApiError(error, 'خطا در حذف تنوع')
            // Re-add the variation if deletion failed
            this.item.variations.splice(index, 0, removedVariation)
          })
          .finally(() => (this.loading = false))
      }
    },

    clearForm() {
      this.step = 0
      this.item = {
        title: '',
        slug: '',
        description: '',
        dimensions: '',
        categories: [],
        variations: [],
        images: [],
      }
      this.images = []
      this.createdItemId = ''
      this.hideError()
      this.validationErrors = {}
      this.uploadProgress = {
        show: false,
        current: 0,
        total: 0,
        failed: 0,
        successful: 0,
      }
      this.$refs.form?.reset()
    },

    handleDeleteImage(url) {
      this.loading = true
      this.$store
        .dispatch('deleteImage', url)
        .then(() => {
          // Refresh product data to get updated images list
          this.fetchUpdatedProduct()
          this.showError('تصویر با موفقیت حذف شد', 'success')
        })
        .catch((error) => {
          this.handleApiError(error, 'خطا در حذف تصویر')
        })
        .finally(() => (this.loading = false))
    },

    onDragStart() {
      // Add class to body for global styling
      document.body.classList.add('dragging')
    },

    onDragEnd() {
      // Remove dragging class
      document.body.classList.remove('dragging')

      // Save the new order
      if (!this.itemId) return

      this.loading = true
      this.$store
        .dispatch('reorderImages', {
          product_id: this.itemId,
          images: this.item.images,
        })
        .then(() => {
          this.showError('ترتیب تصاویر با موفقیت بروزرسانی شد', 'success')
        })
        .catch((error) => {
          this.handleApiError(error, 'خطا در بروزرسانی ترتیب تصاویر')
          // Refresh to restore original order if failed
          this.fetchUpdatedProduct()
        })
        .finally(() => (this.loading = false))
    },

    handleFileChange(files) {
      console.log('Files selected:', files)
      if (files && files.length > 0) {
        // Validate file types and sizes
        const maxSize = 10 * 1024 * 1024 // 10MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

        for (let file of files) {
          if (file.size > maxSize) {
            this.showError('حجم تصاویر نباید بیش از ۱۰ مگابایت باشد', 'warning')
            return
          }
          if (!allowedTypes.includes(file.type)) {
            this.showError('فرمت تصاویر باید JPEG، PNG، GIF یا WebP باشد', 'warning')
            return
          }
        }

        this.images = files
        console.log('Images array updated:', this.images)
      }
    },

    fetchUpdatedProduct() {
      this.loading = true
      this.$store
        .dispatch('showProduct', this.itemId)
        .then((res) => {
          // Update the item with fresh data including new images
          this.item = res.data
          this.createdItemId = res.data.id

          // Ensure variations array exists and is properly formatted
          if (!this.item.variations) {
            this.item.variations = []
          }

          // Ensure each variation has all required fields
          this.item.variations = this.item.variations.map((variation) => {
            // Handle colors - support both old format (single color string) and new format (colors array)
            let colors = []
            if (variation.colors && Array.isArray(variation.colors)) {
              // New format: colors is already an array
              colors = variation.colors.map((color) => {
                // If it's an object, find matching color in colorOptions, otherwise return as string
                if (typeof color === 'object' && color.name) {
                  return color
                } else {
                  // Find color object by name
                  const colorObj = this.colorOptions.find((c) => c.name === color || c.id === color)
                  return colorObj || { name: color, hex: '#000000' }
                }
              })
            } else if (variation.color) {
              // Old format: single color string, convert to array
              const colorObj = this.colorOptions.find((c) => c.name === variation.color)
              if (colorObj) {
                colors = [colorObj]
              } else {
                colors = [{ name: variation.color, hex: '#000000' }]
              }
            }

            // Handle material - can be material_id (number) or material object
            let materialId = null
            if (variation.material_id) {
              materialId = variation.material_id
            } else if (variation.material && variation.material.id) {
              materialId = variation.material.id
            }

            return {
              id: variation.id || null,
              price: variation.price || '',
              colors: colors,
              material_id: materialId,
              size: variation.size || '',
              stock: 1, // Default stock value
              active: variation.active !== undefined ? variation.active : true,
            }
          })

          console.log('Product data refreshed with new images:', this.item.images)
        })
        .catch((error) => {
          this.handleApiError(error, 'خطا در بارگذاری اطلاعات محصول')
        })
        .finally(() => (this.loading = false))
    },

    onColorsChange(variation, selectedColors) {
      // selectedColors is an array of color objects
      if (selectedColors && Array.isArray(selectedColors)) {
        variation.colors = selectedColors.filter((color) => {
          // Filter out custom color option if selected
          return !color.isCustom
        })
      }
    },

    loadProductData(productId, isCopyMode = false) {
      this.loading = true
      this.$store
        .dispatch('showProduct', productId)
        .then((res) => {
          const productData = res.data

          if (isCopyMode) {
            // Copy mode: clear ID and modify slug/title to indicate it's a copy
            this.item = {
              ...productData,
              id: null, // Clear ID so it's treated as new product
              slug: productData.slug ? `${productData.slug}-کپی` : '',
              title: productData.title ? `${productData.title} (کپی)` : '',
            }
            this.createdItemId = '' // Clear createdItemId in copy mode
          } else {
            // Edit mode: keep everything as is
            this.item = productData
            this.createdItemId = productData.id
          }

          // Ensure variations array exists and is properly formatted
          if (!this.item.variations) {
            this.item.variations = []
          }

          // Ensure each variation has all required fields
          // In copy mode, clear variation IDs so they're treated as new variations
          this.item.variations = this.item.variations.map((variation) => {
            // Handle colors - support both old format (single color string) and new format (colors array)
            let colors = []
            if (variation.colors && Array.isArray(variation.colors)) {
              // New format: colors is already an array
              colors = variation.colors.map((color) => {
                // If it's an object, return it, otherwise find matching color in colorOptions
                if (typeof color === 'object' && color.name) {
                  return color
                } else {
                  // Find color object by name or id
                  const colorObj = this.colorOptions.find((c) => c.name === color || c.id === color)
                  return colorObj || { name: color, hex: '#000000' }
                }
              })
            } else if (variation.color) {
              // Old format: single color string, convert to array
              const colorObj = this.colorOptions.find((c) => c.name === variation.color)
              if (colorObj) {
                colors = [colorObj]
              } else {
                colors = [{ name: variation.color, hex: '#000000' }]
              }
            }

            // Handle material - can be material_id (number) or material object
            let materialId = null
            if (variation.material_id) {
              materialId = variation.material_id
            } else if (variation.material && variation.material.id) {
              materialId = variation.material.id
            }

            return {
              id: isCopyMode ? null : variation.id || null, // Clear ID in copy mode
              price: variation.price || '',
              colors: colors,
              material_id: materialId,
              size: variation.size || '',
              stock: variation.stock || '',
              active: typeof variation.active === 'boolean' ? variation.active : true,
            }
          })
        })
        .catch((error) => {
          this.handleApiError(error, 'خطا در بارگذاری اطلاعات محصول')
        })
        .finally(() => (this.loading = false))
    },
  },

  watch: {
    itemId: {
      handler() {
        this.clearForm()
        if (this.itemId) {
          this.loadProductData(this.itemId, false)
        }
      },
      immediate: true,
    },
    copyFromId: {
      handler() {
        if (this.copyFromId && !this.itemId) {
          // Only load if we're in copy mode (copyFromId set but no itemId)
          this.clearForm()
          this.loadProductData(this.copyFromId, true)
        }
      },
      immediate: true,
    },
  },

  mounted() {
    // Fetch colors and materials when component mounts
    this.$store.dispatch('fetchColors')
    this.$store.dispatch('fetchMaterials')
  },

  destroyed() {
    this.clearForm()
    // Clean up dragging class if component is destroyed while dragging
    document.body.classList.remove('dragging')
  },
}
</script>

<style scoped>
.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #e0e0e0;
  display: inline-block;
}

.image-col {
  padding: 8px !important;
  min-height: 150px;
  transition: all 0.2s ease;
}

.image-col::after {
  content: '';
  position: absolute;
  right: -4px;
  top: 0;
  bottom: 0;
  width: 8px;
  background: transparent;
  transition: all 0.2s ease;
}

/* Show drop zone when dragging */
.sortable-drag ~ .image-col::after,
.sortable-ghost ~ .image-col::after {
  background: rgba(33, 150, 243, 0.2);
  width: 12px;
}

/* Highlight valid drop zones when dragging */
body.dragging .image-col {
  background: rgba(33, 150, 243, 0.05);
  border-radius: 8px;
}

body.dragging .image-col:hover {
  background: rgba(33, 150, 243, 0.15);
  transform: scale(1.02);
}

.drag-handle {
  cursor: move !important;
  cursor: grab !important;
  transition: all 0.2s ease;
}

.drag-handle:active {
  cursor: grabbing !important;
}

.drag-handle:hover {
  transform: scale(1.1);
  background: rgba(33, 150, 243, 0.1) !important;
}

/* When item is being dragged */
.dragging-image {
  opacity: 0.8;
  transform: rotate(3deg);
}

/* When item is chosen (clicked) */
.chosen-image {
  border: 3px solid #2196f3 !important;
  background: rgba(33, 150, 243, 0.1) !important;
  box-shadow: 0 8px 16px rgba(33, 150, 243, 0.4) !important;
}

/* Ghost placeholder */
.ghost-image {
  opacity: 0.4;
  background: #e3f2fd !important;
  border: 3px dashed #2196f3 !important;
  border-radius: 8px;
}

.image-item {
  transition: all 0.3s ease;
  border-radius: 8px;
  border: 2px solid transparent;
}

.image-hover {
  filter: brightness(0.8);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
</style>
